import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card } from "@/components/ui/card";

import type { TimeseriesPoint } from "../api";

/** The one chart that explains most of the monitor: BOTH clocks overlaid.
 *  Business events (shaded area, TRANSACTION_WHEN) vs ingest arrivals (line,
 *  CREATED_WHEN). A flat business line under a spiky arrival line IS the
 *  POC's false-alarm class made visible. UsageTimeline conventions. */
export function DualClockChart({
  points,
  title = "Daily volume — both clocks",
}: {
  points: TimeseriesPoint[];
  title?: string;
}) {
  if (points.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-sm text-muted-foreground">No volume in this window — expand the drawer for the 180-day view.</p>
      </Card>
    );
  }
  const data = points.map((p) => ({
    label: shortDay(p.day),
    events: p.event_count ?? 0,
    arrivals: p.arrival_count ?? 0,
  }));
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h2>
        <span className="text-xs text-muted-foreground">
          Business events (shaded) · ingest arrivals (line)
        </span>
      </div>
      <div className="h-56 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
            <defs>
              <linearGradient id="im-events" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--cinch-brand))" stopOpacity={0.28} />
                <stop offset="100%" stopColor="hsl(var(--cinch-brand))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              className="text-muted-foreground"
              axisLine={false}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              className="text-muted-foreground"
              axisLine={false}
              tickLine={false}
              width={48}
            />
            <Tooltip
              contentStyle={{
                fontSize: 12,
                borderRadius: 8,
                border: "1px solid hsl(var(--border))",
                background: "hsl(var(--card))",
              }}
              formatter={(value: number, name: string) => [
                value.toLocaleString(),
                name === "events" ? "business events" : "arrivals",
              ]}
            />
            <Area
              type="monotone"
              dataKey="events"
              stroke="hsl(var(--cinch-brand))"
              strokeWidth={1.5}
              fill="url(#im-events)"
            />
            <Line
              type="monotone"
              dataKey="arrivals"
              stroke="hsl(var(--cinch-power))"
              strokeWidth={1.5}
              dot={false}
              strokeDasharray="4 3"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function shortDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}
